import { useState, useEffect } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useLanguage } from '../../contexts/LanguageContext'; // Import useLanguage hook
import { useLocation, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import { Navigation, Pagination, Autoplay, Zoom, Mousewheel } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

const SimpleServiceDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { language, updateDocumentTitle } = useLanguage();
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const result = useData();
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const isRTL = language === 'ar';
  const { site_info, navigation, contact_info, social_media } = result.data;

  const findServiceBySlug = (services, targetSlug) =>
    services ? Object.values(services).find(service => service.slug === targetSlug) : null;

  const service = findServiceBySlug(result.data?.services, slug) || {};
  const gallery_images = service?.gallery_images || [];
  const features = service?.features || [];

  const location = useLocation();
  const navigate = useNavigate();


  const services = Array.isArray(result.data?.services)
    ? result.data.services
    : Object.values(result.data?.services || {});

  const scrollConfig = {
    smooth: true,
    duration: 800, // Increased duration for smoother scroll
    spy: true,
    offset: -100, // Adjusted offset
    activeClass: 'active1',
    ignoreCancelEvents: false,
    spyThrottle: 500
  }

  // Add this function to handle active state
  const [activeSection, setActiveSection] = useState('home')

  const handleSetActive = (to) => {
    setActiveSection(to)
  }

  const closeMenu = () => setIsOpen(false)

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const LinkContent = ({ item }) => (
    <>
      <span>{item.title}</span>
      {item.icon && <FontAwesomeIcon icon={item.icon} className="ml-2" />}
    </>
  );



  const handleNavigation = (item) => {
    closeMenu();
    //console.log('Navigation item:', item);
    //console.log('Current location:', location.pathname);

    if (item.to && location.pathname !== '/') {
      // First navigate to homepage
      navigate(item.href);

      // Then set up a timeout to scroll after navigation is complete
      setTimeout(() => {
        const element = document.getElementById(item.to);
        if (element) {
          const yOffset = -100;
          const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: yPosition, behavior: 'smooth' });
          setActiveSection(item.to);
        }
      }, 100); // Small delay to ensure DOM is updated
    } else if (item.to && location.pathname === '/') {
      // If already on homepage, just scroll to section
      const element = document.getElementById(item.to);
      if (element) {
        const yOffset = -100;
        const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: yPosition, behavior: 'smooth' });
        setActiveSection(item.to);
      }
    } else if (item.href) {
      // Regular navigation to other pages
      navigate(item.href);
      setActiveSection(item.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Handle scroll restoration and navigation state
    if (location.state?.scrollTo) {
      const elementId = location.state.scrollTo === 'root' ? 'root' : location.state.scrollTo; // Handle 'root' as 'root'
      //console.log('Restoring scroll to section:', elementId);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const yOffset = -100; // Adjust this value based on your header height
          const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          //console.log('Restoring scroll to position:', yPosition);
          window.scrollTo({ top: yPosition, behavior: 'smooth' });
        } else {
          //console.log('Element not found for restoration:', elementId);
        }
        // Clear the state
        navigate(location.pathname, { replace: true, state: {} });
      }, 500);
    } else {
      setActiveSection(location.pathname);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (result.data?.site_info && service?.title) {
      updateDocumentTitle(service.title, result.data.site_info);
    }
  }, [service, result.data, language, updateDocumentTitle]);

  const NavigationItem = ({ item, className }) => {
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    if (item.to && isHomePage) {
      return (
        <ScrollLink
          to={item.to}
          {...scrollConfig}
          onSetActive={() => handleSetActive(item.to)}
          className={className}
          onClick={() => handleNavigation(item)}
        >
          <LinkContent item={item} />
        </ScrollLink>
      );
    }

    return (
      <RouterLink
        to={item.href || '/'}
        onClick={() => handleNavigation(item)}
        className={className}
      >
        <LinkContent item={item} />
      </RouterLink>
    );
  };

  const ImageModal = ({ onClose }) => (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-90">
      <div className="relative px-4 w-full max-w-6xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 text-white hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} className="text-2xl" />
        </button>
        <Swiper
          modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
          dir={isRTL ? 'rtl' : 'ltr'}
          spaceBetween={0}
          slidesPerView={1}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          pagination={{
            clickable: true,
            el: '.swiper-pagination'
          }}
          loop={true}
          autoplay={{
            enabled: true,
            delay: 3000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          }}
          mousewheel={{
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
            sensitivity: 2,
          }}
          keyboard={true}
          initialSlide={currentImage}
          className="modal-swiper"
        >
          {gallery_images.map((img, index) => (
            <SwiperSlide key={index}>
              <div className="flex items-center justify-center h-[80vh]">
                <img src={img} alt="" className="object-contain max-w-full max-h-full" />
              </div>
            </SwiperSlide>
          ))}
          <div className="swiper-button-prev !text-white !text-xl !leading-8"></div>
          <div className="swiper-button-next !text-white !text-xl !leading-8"></div>
          <div className="swiper-pagination !bottom-6"></div>
        </Swiper>
      </div>
    </div>
  );

  if (!service?.slug) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 rounded-full border-t-2 border-b-2 animate-spin border-stone-800"></div>
      </div>
    );
  }

  updateDocumentTitle(service.title, site_info);

  // Add contact menu item
  const contactMenuItem = {
    id: 'contact',
    title: t('footer.contact'),
    href: '/contact',
    icon: faArrowRight
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative h-[85vh]">
        <img
          src={service.image}
          alt={service.title}
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80">
          <div className="container flex flex-col justify-end px-4 pb-24 mx-auto h-full">
            <div className="max-w-3xl">
              <h1 className="mb-6 text-6xl font-light tracking-tight text-white">{service.title}</h1>
            </div>
          </div>
        </div>
      </section>

      <div className="container px-4 py-24 mx-auto">
        {/* Main Content Section */}
        <div className="grid gap-16 mb-24 md:grid-cols-2">
          <div className="space-y-8">
            <div className="inline-block pb-2 border-b-2 border-stone-800">
              <h2 className="text-3xl font-light tracking-wide">{t('services.overview')}</h2>
            </div>
            <div className="max-w-none prose prose-lg">
              <p className="leading-relaxed text-gray-700">{service.description}</p>
            </div>
            {features.length > 0 && (
              <div className="space-y-6 mt-12">
                {features.map((feature, index) => (
                  <div key={index} className="flex gap-4 items-start group">
                    <span className="p-1.5 mt-1 text-white rounded-full bg-stone-800 transition-transform group-hover:scale-110">
                      <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5" />
                    </span>
                    <p className="text-gray-700 text-lg">{feature}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <div className="sticky top-24 p-8 space-y-8 bg-white rounded-2xl shadow-xl">
              <img
                src={service.detail_image || service.image}
                alt={service.title}
                className="object-cover w-full h-80 rounded-xl transition-transform hover:scale-[1.02]"
              />
              <div className="space-y-6">
                <NavigationItem
                  item={contactMenuItem}
                  className="inline-flex justify-center items-center px-8 py-5 w-full text-lg font-medium tracking-wide text-white rounded-xl shadow-lg transition-all bg-stone-800 hover:bg-stone-900 hover:shadow-2xl transform hover:-translate-y-0.5"
                >
                  {t('footer.contact')}
                  <FontAwesomeIcon icon={faArrowRight} className="ml-3 transition-transform group-hover:translate-x-1" />
                </NavigationItem>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        {gallery_images.length > 0 && (
          <div className="mb-24">
            <div className="inline-block pb-2 mb-12 border-b-2 border-stone-800">
              <h2 className="text-3xl font-light tracking-wide">{t('services.gallery')}</h2>
            </div>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
              dir={isRTL ? 'rtl' : 'ltr'}
              spaceBetween={20}
              slidesPerView={1}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination'
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              keyboard={true}
              mousewheel={{
                enabled: true,
                forceToAxis: true,
                releaseOnEdges: true,
                sensitivity: 2,
              }}
              loop={true}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="gallery-swiper !overflow-visible"
            >
              {gallery_images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="aspect-[4/3] cursor-pointer rounded-2xl overflow-hidden shadow-lg transform transition-all hover:-translate-y-1 hover:shadow-xl"
                    onClick={() => {
                      setCurrentImage(index);
                      setShowModal(true);
                    }}
                  >
                    <img
                      src={image}
                      alt=""
                      className="object-cover w-full h-full transition-transform hover:scale-105"
                    />
                  </div>
                </SwiperSlide>
              ))}
              <div className="swiper-button-prev !text-white !text-xl !leading-8"></div>
              <div className="swiper-button-next !text-white !text-xl !leading-8"></div>
              <div className="swiper-pagination !bottom-6"></div>
            </Swiper>
          </div>
        )}
      </div>

      {showModal && (
        <ImageModal
          image={gallery_images[currentImage]}
          onClose={() => setShowModal(false)}
          initialSlide={currentImage}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default SimpleServiceDetail;
